import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Spinner, Table, Modal } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// Helper: model resolver like your VehicleList
const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? v?.strMake ?? '').trim();
  const factory2 = factory.toLowerCase().includes('ford') ? 'Ford' : factory.toLowerCase().includes('gm') ? 'GM' : factory;
  const model = (v?.strModel ?? '').trim();
  if (!factory && !model) return '';
  const k1 = `${factory2} - ${model}`;
  const k2 = `${factory2} - ${model.substring(0, 3)}`;
  return modelCodes[k1] || modelCodes[k2] || model || '';
};

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  if (ts instanceof Date) return ts;
  return null;
};

const ageBucket = (ageDays) => {
  if (ageDays == null) return null;
  if (ageDays <= 30) return '0_30';
  if (ageDays <= 90) return '31_90';
  if (ageDays <= 180) return '91_180';
  return 'gt_180';
};

const BUCKET_HEADERS = [
  { key: '0_30', label: '30' },
  { key: '31_90', label: '31-90' },
  { key: '91_180', label: '91-180' },
  { key: 'gt_180', label: '>180' },
];

const display = (n) => (n > 0 ? n : '–');

export default function InventoryAgeReport() {
  const { onSnapshotQueryData, getData } = useFirestore();

  const [vehicles, setVehicles] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalDetail, setModalDetail] = useState(null);

  // live vehicles for SO === 'Stock'
  useEffect(() => {
    const unsub = onSnapshotQueryData(
      'vehicles',
      'strSalesOrdNbr', '==', 'Stock',
      (data) => {
        setVehicles(data || []);
        setLoading(false);
      }
    );
    return () => unsub && unsub();
  }, [onSnapshotQueryData]);

  // model codes map
  useEffect(() => {
    (async () => {
      try {
        const data = await getData('modelCodes');
        const map = (data || []).reduce((acc, obj) => {
          acc[`${obj.strFactory} - ${obj.strModel}`] = obj.strModelName;
          return acc;
        }, {});
        setModelCodes(map);
      } catch {
        /* optional */
      }
    })();
  }, [getData]);

  // Compute once per change
  const { summaryByFactory, detailByFactory, grandTotal, vehiclesByFactoryBucket, detailVehiclesByFactory } = useMemo(() => {
    const now = new Date();
    const countsTemplate = () => ({
      '0_30': 0,
      '31_90': 0,
      '91_180': 0,
      'gt_180': 0,
      total: 0,
    });
    const bucketListTemplate = () => ({
      '0_30': [],
      '31_90': [],
      '91_180': [],
      'gt_180': [],
    });

    const byFactory = { Ford: [], GM: [] };
    const bucketVehicles = { Ford: bucketListTemplate(), GM: bucketListTemplate() };

    // Only consider vehicles that have a dtRecvDate; ignore others
    (vehicles || []).forEach((v) => {
      const recv = toDate(v.dtRecvDate);
      if (!recv) return;
      const age = Math.floor((now - recv) / (1000 * 60 * 60 * 24));
      const bucket = ageBucket(age);
      if (!bucket) return;

      const factory = (v.strFactory || '').trim();
      // Normalize anything that isn't Ford to GM if you want strict two rows:
      const group = factory.toLowerCase().includes('ford') ? 'Ford' : 'GM';

      const modelName = resolveModelName(v, modelCodes) || '(Unknown Model)';
      const vehicleInfo = {
        id: v.id || `${v.strSalesOrdNbr || 'Stock'}-${v.strVin || Math.random()}`,
        stockNumber: v.strStockNbr || (v.strVin ? v.strVin.slice(-8) : ''),
        year: v.strYR || '',
        make: v.strMake || '',
        model: modelName,
      };

      byFactory[group].push({ ...v, _bucket: bucket, _modelName: modelName, _vehicleInfo: vehicleInfo });
      bucketVehicles[group][bucket] = [...bucketVehicles[group][bucket], vehicleInfo];
    });

    // Summary per factory
    const summary = { Ford: countsTemplate(), GM: countsTemplate() };
    Object.entries(byFactory).forEach(([factory, arr]) => {
      arr.forEach((v) => {
        summary[factory][v._bucket] += 1;
        summary[factory].total += 1;
      });
    });

    // Detail per factory → per model
    const detail = { Ford: {}, GM: {} };
    const detailVehicles = { Ford: {}, GM: {} };
    Object.entries(byFactory).forEach(([factory, arr]) => {
      arr.forEach((v) => {
        const name = v._modelName || '(Unknown Model)';
        if (!detail[factory][name]) detail[factory][name] = countsTemplate();
        if (!detailVehicles[factory][name]) detailVehicles[factory][name] = bucketListTemplate();
        detail[factory][name][v._bucket] += 1;
        detail[factory][name].total += 1;
        detailVehicles[factory][name][v._bucket] = [
          ...detailVehicles[factory][name][v._bucket],
          v._vehicleInfo,
        ];
      });
    });

    const grand = countsTemplate();
    ['Ford', 'GM'].forEach((f) => {
      BUCKET_HEADERS.forEach(({ key }) => (grand[key] += summary[f][key]));
      grand.total += summary[f].total;
    });

    return {
      summaryByFactory: summary,
      detailByFactory: detail,
      grandTotal: grand,
      vehiclesByFactoryBucket: bucketVehicles,
      detailVehiclesByFactory: detailVehicles,
    };
  }, [vehicles, modelCodes]);

  const openModal = (title, vehiclesList) => {
    if (!vehiclesList || vehiclesList.length === 0) return;
    setModalDetail({
      title,
      vehicles: vehiclesList,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalDetail(null);
  };

  const handleSummaryCellClick = (factory, bucketKey) => {
    const vehiclesList = vehiclesByFactoryBucket?.[factory]?.[bucketKey] || [];
    openModal(`${factory} — ${BUCKET_HEADERS.find(b => b.key === bucketKey)?.label || bucketKey} Days`, vehiclesList);
  };

  const handleDetailCellClick = (factory, modelName, bucketKey) => {
    const vehiclesList = detailVehiclesByFactory?.[factory]?.[modelName]?.[bucketKey] || [];
    openModal(`${factory} • ${modelName} — ${BUCKET_HEADERS.find(b => b.key === bucketKey)?.label || bucketKey} Days`, vehiclesList);
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const renderBlock = (title, dataObj, factoryKey, showTotalRow = true) => {
    const rows = Object.keys(dataObj).sort((a, b) => a.localeCompare(b));
    const totalRow = rows.reduce(
      (acc, name) => {
        BUCKET_HEADERS.forEach(({ key }) => (acc[key] += dataObj[name][key]));
        acc.total += dataObj[name].total;
        return acc;
      },
      { '0_30': 0, '31_90': 0, '91_180': 0, 'gt_180': 0, total: 0 }
    );

    return (
      <Card className="mb-4">
        <Card.Header className="fw-bold">{title}</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered hover className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '40%' }}></th>
                {BUCKET_HEADERS.map(({ label }) => <th key={label} className="text-center">{label}</th>)}
                <th className="text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((name) => (
                <tr key={name}>
                  <td>{name}</td>
                  {BUCKET_HEADERS.map(({ key }) => (
                    <td key={key} className="text-center">
                      {dataObj[name][key] > 0 ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => handleDetailCellClick(factoryKey, name, key)}
                        >
                          {dataObj[name][key]}
                        </button>
                      ) : (
                        display(dataObj[name][key])
                      )}
                    </td>
                  ))}
                  <td className="text-center fw-semibold">{display(dataObj[name].total)}</td>
                </tr>
              ))}
              {showTotalRow && (
                <tr>
                  <td className="fw-semibold">Total</td>
                  {BUCKET_HEADERS.map(({ key }) => (
                    <td key={key} className="text-center fw-semibold">{display(totalRow[key])}</td>
                  ))}
                  <td className="text-center fw-bold">{display(totalRow.total)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {/* Top summary like your screenshot */}
      <Card className="mb-4">
        <Card.Header className="fw-bold">Inventory Age — Stock Inventory</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered hover className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '40%' }}></th>
                {BUCKET_HEADERS.map(({ label }) => <th key={label} className="text-center">{label}</th>)}
                <th className="text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {['Ford', 'GM'].map((make) => (
                <tr key={make}>
                  <td className="fw-semibold">{make}</td>
                  {BUCKET_HEADERS.map(({ key }) => (
                    <td key={key} className="text-center">
                      {summaryByFactory[make][key] > 0 ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => handleSummaryCellClick(make, key)}
                        >
                          {summaryByFactory[make][key]}
                        </button>
                      ) : (
                        display(summaryByFactory[make][key])
                      )}
                    </td>
                  ))}
                  <td className="text-center fw-bold">{display(summaryByFactory[make].total)}</td>
                </tr>
              ))}
              <tr>
                <td className="fw-bold">Total</td>
                {BUCKET_HEADERS.map(({ key }) => (
                  <td key={key} className="text-center fw-bold">{display(grandTotal[key])}</td>
                ))}
                <td className="text-center fw-bold">{display(grandTotal.total)}</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Per-factory details */}
      <Row>
        <Col lg={12} className="mb-2">
          {renderBlock('Ford — Stock Inventory', detailByFactory.Ford, 'Ford')}
        </Col>
        <Col lg={12}>
          {renderBlock('GM — Stock Inventory', detailByFactory.GM, 'GM')}
        </Col>
      </Row>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalDetail?.title || 'Vehicles'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalDetail?.vehicles?.length ? (
            <Table bordered responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 140 }}>Stock #</th>
                  <th style={{ minWidth: 80 }}>Year</th>
                  <th style={{ minWidth: 140 }}>Make</th>
                  <th style={{ minWidth: 160 }}>Model</th>
                </tr>
              </thead>
              <tbody>
                {modalDetail.vehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td>
                      {vehicle.stockNumber ? (
                        <a href={`/vehicles?search=${encodeURIComponent(vehicle.stockNumber)}&status=all`}>
                          {vehicle.stockNumber}
                        </a>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>{vehicle.year || '–'}</td>
                    <td>{vehicle.make || '–'}</td>
                    <td>{vehicle.model || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted">No vehicles found for this selection.</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
